import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';

import { Button, Col, Modal, Row, Spin, Tooltip } from 'antd';

import ColoredFormattedNumberForeign from 'components/ColoredFormattedNumberForeign';
import ModalActions from 'components/ModalActions';
import { useBankTransfer } from 'components/Transaction/useBankTransfer';
import { constants, FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import { useProfileListAccounts } from 'v2/hooks/useProfileListAccounts';
import { useUserStore } from 'v2/store/User';

import AccountList from './components/AccountList';

const MIN_VALUE_TO_TRANSFER_DOLAR = 1000;
const MIN_VALUE_TO_TRANSFER_REAL = 5500;
const MIN_TRANSFER_VALUE_USD = 100;

interface IProps {
  value: any;
  selfClose: any;
  successCallback: any;
  open: boolean;
  onCloseModalTransfer: any;
}

const TransferForeign = ({ value, selfClose, successCallback, open, onCloseModalTransfer }: IProps) => {
  const [modalTransfers, setModalTransfers] = useState(false);
  const [changeAccountList, setChangeAccountList] = useState(false);
  const [accountActive, setAccountActive] = useState<any>({});
  const [disableTransfer, setDisableTransfer] = useState(false);
  const { data: accounts, isLoading: isLoadingAccounts } = useProfileListAccounts(true);

  const { crossborder } = useUserStore(({ user }) => ({ crossborder: user.crossborder }));

  const accountActiveRef = useRef(accountActive);
  accountActiveRef.current = accountActive;

  const minAbleToTransfer = crossborder ? MIN_VALUE_TO_TRANSFER_DOLAR : MIN_VALUE_TO_TRANSFER_REAL;
  const currency = crossborder ? 'USD' : 'BRL';

  const { bankTransfer, isPending: isPendingBankTransfer } = useBankTransfer();

  useEffect(() => {
    if (value < minAbleToTransfer) {
      setDisableTransfer(true);
    }
  }, [value, minAbleToTransfer]);

  useEffect(() => {
    if (!isLoadingAccounts && accounts?.length > 0) {
      setAccountActive(accounts[0]);
    }
  }, [accounts, isLoadingAccounts]);

  const handleClickTransfers = useCallback(
    (transferValue: number) => {
      const account = accounts.length ? accountActiveRef.current.id : null;
      setModalTransfers(false);

      let success = true;

      if (!account) {
        success = false;
        toast.error('Por favor cadastre uma conta bancária.');
      }

      if (transferValue < 0) {
        success = false;
        toast.error('Valor inválido para transferência.');
      }

      if (transferValue < MIN_TRANSFER_VALUE_USD) {
        success = false;
        toast.error('O valor da transferência não pode ser menor que US$ 100,00.');
      }

      if (success) {
        (async () => {
          try {
            if (transferValue > 0) {
              setDisableTransfer(true);
              await bankTransfer({
                value: transferValue,
                transfer_method_id: account,
                commissions: []
              });
            }

            if (successCallback) {
              successCallback(true);
            }

            toast.success('Sucesso, transferência realizada com êxito');

            if (successCallback) {
              successCallback(false);
            }

            selfClose();
          } catch (err) {
            toast.error('Ops, erro na transferência');
          } finally {
            setDisableTransfer(false);
          }
        })();
      }
    },
    [accounts, successCallback, selfClose, bankTransfer]
  );

  const handleOnClick = useCallback(() => {
    setModalTransfers(true);
  }, []);

  const onCancelModalTransfers = useCallback(() => {
    setModalTransfers(false);
  }, []);

  const handleChangeAccount = useCallback((account: any) => {
    setAccountActive(account);
    setChangeAccountList(prev => !prev);
  }, []);

  const toggleChangeAccount = useCallback(() => {
    setChangeAccountList(prev => !prev);
  }, []);

  const renderTooltipValue = useMemo(() => {
    if (value < minAbleToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={minAbleToTransfer} format={currency}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [value, minAbleToTransfer, currency]);

  return (
    <>
      <Modal
        open={open}
        onCancel={onCloseModalTransfer}
        footer={null}
        centered
        width={650}
        style={{ maxWidth: '95vw' }}
        styles={{ body: { maxHeight: '80vh', overflow: 'auto' } }}
      >
        <div className={changeAccountList ? 'modal__transaction --flip' : 'modal__transaction'}>
          {!changeAccountList && (
            <Row gutter={0} className='transacation-list__accounts'>
              <Col>
                <div className='pr-2'>
                  <h2>Transferência bancária</h2>
                </div>
              </Col>
              <div className='w-full border-b border-[#ddd] px-4 py-2 max-sm:px-2 max-sm:py-4 max-sm:pb-6 max-sm:text-right'>
                <div className='flex w-full items-baseline justify-between text-base max-sm:flex-wrap'>
                  <div className={'amount__checkbox'}>
                    <span className='label' style={{ margin: 30 }}>
                      Valor a transferir:
                    </span>
                  </div>
                  <div className={'amount__value'}>
                    <ColoredFormattedNumberForeign value={value || 10} format={currency} />
                  </div>
                </div>
              </div>
              <div className='mt-[10px] flex w-full flex-row justify-between text-[15px] [&_span]:font-normal max-sm:[&_span]:font-semibold'>
                {!isLoadingAccounts && accounts?.length > 0 && (
                  <div>
                    Transferir para Banco: <span>{accountActive.bank_id}</span>, Ag:{' '}
                    <span>
                      {accountActive.agency}-{accountActive.agency_digit}
                    </span>
                    , Conta:{' '}
                    <span>
                      {accountActive.account}-{accountActive.account_digit}
                    </span>
                    .
                    <p
                      onClick={toggleChangeAccount}
                      style={{
                        cursor: 'pointer',
                        color: '#3f51b5',
                        fontSize: '14px',
                        lineHeight: '14px',
                        marginTop: 5,
                        marginBottom: 0
                      }}
                    >
                      Clique aqui para alterar a conta
                    </p>
                  </div>
                )}
                {isLoadingAccounts && (
                  <div style={{ width: '100%', textAlign: 'center' }}>
                    <Spin size='small' />
                  </div>
                )}
                {!isLoadingAccounts && (!accounts || accounts.length === 0) && (
                  <div>Nenhuma conta bancária está ativa.</div>
                )}
              </div>
              <WithBetaModules>
                {(betaModules: string[]) => {
                  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
                  const renderTooltipBlockTransfer = userHasBlockTransferBetaModule
                    ? FINANCIAL_BLOCK_TOOLTIP_MESSAGE
                    : false;
                  return (
                    <Tooltip title={renderTooltipBlockTransfer || renderTooltipValue} placement='top'>
                      <div className='mt-4 w-full'>
                        <Button
                          type='primary'
                          className='bg-[#009358]! text-white hover:bg-[#036b41]!'
                          block
                          onClick={handleOnClick}
                          disabled={disableTransfer || userHasBlockTransferBetaModule}
                        >
                          Transferir
                          {isPendingBankTransfer && <Spin size='small' className='ml-2.5' />}
                        </Button>
                      </div>
                    </Tooltip>
                  );
                }}
              </WithBetaModules>
            </Row>
          )}

          {changeAccountList && (
            <AccountList
              handleChangeAccount={handleChangeAccount}
              accountDefault={accountActive}
              listAccounts={accounts}
              toggleChangeAccount={toggleChangeAccount}
            />
          )}
        </div>
      </Modal>

      <ModalActions
        open={modalTransfers}
        onClose={onCancelModalTransfers}
        onOk={() => handleClickTransfers(value)}
        title={'Atenção'}
        buttonOk={'Continuar'}
        loadingColor='#ccc'
      >
        <p>
          Você solicitou uma transferência.
          <br />
          Neste valor será descontado os encargos de transferência no valor de{' '}
          <ColoredFormattedNumberForeign value={constants.TRANSFER_RATE_VALUE} format={currency} />. Então o valor total
          transferido para sua conta será de
          <b>
            <ColoredFormattedNumberForeign value={value - constants.TRANSFER_RATE_VALUE} format={currency} />
          </b>
          .
          <br />
          Deseja continuar com a transação?
        </p>
      </ModalActions>
    </>
  );
};

export default TransferForeign;
